import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  Banknote, TrendingUp, Users, ShieldCheck, AlertTriangle,
  CheckCircle2, FileText, Loader2, Mail,
} from "lucide-react";
import api from "../../lib/api";
import ZClosesHistoryDialog from "./ZClosesHistoryDialog";

// Per-register performance card — compact 30-day KPIs + top operators.

function StatTile({ label, value, sub, tone = "gray" }) {
  const toneCls = {
    gray:    "text-foreground",
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    rose:    "text-rose-700",
    violet:  "text-violet-700",
  }[tone] || "text-foreground";
  return (
    <div className="p-2 rounded border bg-card">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-base font-bold tabular-nums ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function RegisterDashboardCard({ restaurantId, register }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId || !register?.register_id) return;
    setLoading(true);
    try {
      const r = await api.getRegisterDashboard(restaurantId, register.register_id, 30);
      setData(r);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId, register]);

  useEffect(() => { load(); }, [load]);

  const fmt = (v) => typeof v === "number" ? `${v.toFixed(2)}€` : "—";
  const hasActivity = data && data.total_arqueos > 0;
  const balancedTone = !data ? "gray" : data.balanced_pct >= 80 ? "emerald" : data.balanced_pct >= 50 ? "amber" : "rose";
  const discTone = !data ? "gray" : Math.abs(data.total_discrepancy) < 5 ? "emerald" : Math.abs(data.total_discrepancy) < 25 ? "amber" : "rose";

  return (
    <Card className="overflow-hidden" data-testid={`register-dashboard-${register?.register_id}`}>
      <div
        className="px-3 py-2 flex items-center gap-2 border-b"
        style={{ backgroundColor: `${register?.color || "#8b5cf6"}15` }}
      >
        <div className="h-6 w-6 rounded-full shrink-0" style={{ backgroundColor: register?.color || "#8b5cf6" }} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold">{register?.name}</div>
          {register?.zone && <div className="text-[10px] text-muted-foreground">{register.zone}</div>}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 gap-1 px-2 text-[11px]"
          onClick={() => setHistoryOpen(true)}
          data-testid={`register-z-history-${register?.register_id}`}
        >
          <Mail className="h-3 w-3" />
          Historial Z
        </Button>
        <div className="text-[10px] text-muted-foreground">30d</div>
      </div>
      <CardContent className="p-3">
        {loading ? (
          <div className="text-center py-3 text-[11px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando…
          </div>
        ) : !hasActivity ? (
          <div className="text-center py-3 text-[11px] text-muted-foreground">
            Sin arqueos cerrados en los últimos 30 días.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <StatTile
                label="Ventas"
                value={fmt(data.total_sales)}
                sub={`${data.total_transactions} tickets`}
                tone="violet"
              />
              <StatTile
                label="Arqueos"
                value={data.total_arqueos}
                sub={`${data.balanced_pct}% cuadrados`}
                tone={balancedTone}
              />
              <StatTile
                label="Descuadre total"
                value={fmt(data.total_discrepancy)}
                sub={`Media ${fmt(data.avg_discrepancy)}`}
                tone={discTone}
              />
              <StatTile
                label="Propinas"
                value={fmt(data.total_tips)}
                sub={data.last_z_number ? `Último Z-${String(data.last_z_number).padStart(3, "0")}` : "—"}
                tone="gray"
              />
            </div>
            {/* Balance breakdown */}
            <div className="grid grid-cols-3 gap-1.5 text-[10px]">
              <div className="px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-center">
                <CheckCircle2 className="h-3 w-3 inline mr-0.5" />
                {data.perfectly_balanced} cuadrados
              </div>
              <div className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-center">
                <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                {data.warnings} &lt;10€
              </div>
              <div className="px-2 py-1 rounded bg-rose-50 border border-rose-200 text-rose-800 text-center">
                <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                {data.serious} &gt;10€
              </div>
            </div>
            {/* Top operators */}
            {(data.top_operators || []).length > 0 && (
              <div className="border-t pt-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Top descuadres por operador
                </div>
                <div className="space-y-0.5">
                  {data.top_operators.map((op, idx) => (
                    <div key={op.operator_id} className="flex items-center justify-between text-[11px] px-1 py-0.5 rounded hover:bg-gray-50"
                      data-testid={`register-dashboard-op-${idx}`}>
                      <span className="text-muted-foreground truncate flex-1" title={op.operator_id}>
                        {op.operator_id.startsWith("user_") ? op.operator_id.slice(0, 16) + "…" : op.operator_id}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{op.count} arqueos</span>
                      <span className={`ml-2 font-medium tabular-nums ${Math.abs(op.total_disc) < 1 ? "text-emerald-700" : Math.abs(op.total_disc) < 10 ? "text-amber-700" : "text-rose-700"}`}>
                        {op.total_disc > 0 ? "+" : ""}{op.total_disc.toFixed(2)}€
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
      {historyOpen && (
        <ZClosesHistoryDialog
          open
          onClose={() => setHistoryOpen(false)}
          restaurantId={restaurantId}
          register={register}
        />
      )}
    </Card>
  );
}
